import { css, styled } from 'canoe';
import React, { ChangeEvent, useCallback } from 'react';
import { APP_URL } from 'site-constants';

import { ValueBox, ValueGroup, ValueInput, ValueToggle } from '@candu-ai/craft/ui';
import { useToggle, useWorkspacePath } from 'commons/hooks';
import { Section } from 'craft/containers/Toolbox/components';
import { useMultiNodes } from 'craft/hooks';
import { useFormAncestor } from 'craft/state';
import { FORMS_PATH, RESPONSES_PATH } from 'dashboard/pages/Forms';

import { FormStateToggler } from './FormStateToggler';
import { MultistepFormToolbox } from './MultistepFormToolbox';

const StyledLink = styled.a`
  ${({ theme }) => css`
    color: ${theme.colors.OnyxGrayL30};
    font-size: ${theme.fonts.small.fontSize};
    text-decoration: underline;
    text-align: right;
    margin-top: ${theme.spaces.xxs};
    display: block;

    &:hover {
      color: ${theme.colors.OnyxGrayL20};
      text-decoration: underline;
    }
  `}
`;

interface Props {
  nodeIds: string[];
}

export const FormToolbox = ({ nodeIds }: Props) => {
  const { isInForm, formNodeId } = useFormAncestor((nodeIds || [])[0]);

  const {
    props,
    actions: { setProps },
  } = useMultiNodes([formNodeId ?? ''], (node) => ({
    props: node?.data.props || {},
  }));

  const workspacePath = useWorkspacePath();
  const [redirectOnSubmit, toggleRedirectOnSubmit] = useToggle(!!props.submitRedirectUrl);

  const hasSubmitCooldown = !!props.submitCooldown;

  const onPropChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value, name } = e.target;

      const cleanValue =
        name === 'submitRedirectUrl' ? `https://${value.replace(/^(https|http):\/\//, '')}` : value;

      setProps({
        [name]: cleanValue,
      });
    },
    [setProps],
  );

  if (!formNodeId || !isInForm) {
    return null;
  }

  return (
    <Section name="Form Settings" data-cy="toolbox-section-columns">
      <ValueGroup>
        <ValueBox label="Name">
          <ValueInput
            name="name"
            value={props.name}
            onChange={onPropChange}
            placeholder="Form name"
            hasError={!props.name}
          />
        </ValueBox>
      </ValueGroup>

      <ValueGroup>
        <ValueBox label="Redirect on submission" isExpandable={{ isExpanded: redirectOnSubmit }}>
          <ValueToggle
            toggleGroup="form-submission-redirect"
            onClick={() => {
              if (redirectOnSubmit) {
                setProps({ submitRedirectUrl: undefined });
              }
              toggleRedirectOnSubmit();
            }}
            isActive={redirectOnSubmit}
          />
        </ValueBox>
        {redirectOnSubmit && (
          <ValueBox label="https://" isInGroup={{ isLast: true }}>
            <ValueInput
              name="submitRedirectUrl"
              value={props.submitRedirectUrl?.replace(/^(https|http):\/\//, '')}
              onChange={onPropChange}
              placeholder="yourproduct.com/success"
            />
          </ValueBox>
        )}
      </ValueGroup>

      <ValueGroup>
        <ValueBox label="Prevent resubmission based on User ID">
          <ValueToggle
            toggleGroup="form-submission-cooldown"
            onClick={() => {
              if (hasSubmitCooldown) {
                setProps({ submitCooldown: undefined });
              } else {
                setProps({ submitCooldown: 'infinity' });
              }
            }}
            isActive={hasSubmitCooldown}
          />
        </ValueBox>
      </ValueGroup>

      <ValueGroup>
        <FormStateToggler nodeId={formNodeId} />
        <StyledLink
          href={`${APP_URL}${workspacePath(`${FORMS_PATH}/${RESPONSES_PATH}`)}/${props.formId}`}
          target="_blank"
        >
          Go to Form responses
        </StyledLink>
      </ValueGroup>

      <MultistepFormToolbox nodeId={formNodeId} />
    </Section>
  );
};
